import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, X } from "lucide-react";
import { cn } from "../lib/cn";
import { fadeIn, scaleIn, slideOverRight } from "../lib/motion";
import { Button, IconButton } from "./Button";

/** Locks page scroll and returns focus where it came from. */
function useModalBehaviour(open: boolean, onClose: () => void) {
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCloseRef.current();
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener("keydown", onKeyDown);
      previouslyFocused.current?.focus?.();
    };
  }, [open]);
}

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  /** Set false for flows the user must resolve with a button. */
  dismissOnOverlayClick?: boolean;
  className?: string;
}

const sizes = {
  sm: "max-w-sm",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
} as const;

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
  dismissOnOverlayClick = true,
  className,
}: ModalProps) {
  useModalBehaviour(open, onClose);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-4">
          <motion.div
            variants={fadeIn}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={dismissOnOverlayClick ? onClose : undefined}
            className="absolute inset-0 bg-ink-950/60 backdrop-blur-sm"
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={typeof title === "string" ? title : undefined}
            variants={scaleIn}
            initial="hidden"
            animate="visible"
            exit="exit"
            className={cn(
              "relative flex max-h-[92dvh] w-full flex-col overflow-hidden border border-line bg-surface shadow-ir-lg",
              "rounded-t-2xl sm:rounded-2xl",
              sizes[size],
              className,
            )}
          >
            <span aria-hidden="true" className="h-0.5 w-full rule-gold" />

            {(title || description) && (
              <header className="flex items-start justify-between gap-4 px-6 pt-5 pb-4">
                <div className="min-w-0">
                  {title && (
                    <h2 className="text-lg font-semibold text-content">{title}</h2>
                  )}
                  {description && (
                    <p className="mt-1 text-sm text-content-muted">{description}</p>
                  )}
                </div>
                <IconButton
                  icon={X}
                  label="Close"
                  size="sm"
                  onClick={onClose}
                  className="-mr-2 -mt-1"
                />
              </header>
            )}

            {children && (
              <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">{children}</div>
            )}

            {footer && (
              <footer className="flex flex-col-reverse gap-2 border-t border-line bg-surface-sunken px-6 py-4 sm:flex-row sm:justify-end">
                {footer}
              </footer>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  description: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Destructive actions get the danger button plus a warning glyph. */
  destructive?: boolean;
  loading?: boolean;
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  loading = false,
}: ConfirmDialogProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      dismissOnOverlayClick={!loading}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? "danger" : "primary"}
            onClick={() => void onConfirm()}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex gap-4 pt-4">
        {destructive && (
          <span className="grid size-10 shrink-0 place-items-center rounded-full bg-danger-50 text-danger-600 dark:bg-danger-500/10 dark:text-danger-500">
            <AlertTriangle size={19} aria-hidden="true" />
          </span>
        )}
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-content">{title}</h2>
          <div className="mt-1.5 text-sm text-content-muted">{description}</div>
        </div>
      </div>
    </Modal>
  );
}

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: string;
}

/** Side sheet, used for filters and detail peeks on narrow screens. */
export function Drawer({
  open,
  onClose,
  title,
  children,
  footer,
  width = "max-w-md",
}: DrawerProps) {
  useModalBehaviour(open, onClose);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50">
          <motion.div
            variants={fadeIn}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={onClose}
            className="absolute inset-0 bg-ink-950/60 backdrop-blur-sm"
          />
          <motion.aside
            role="dialog"
            aria-modal="true"
            variants={slideOverRight}
            initial="hidden"
            animate="visible"
            exit="exit"
            className={cn(
              "absolute inset-y-0 right-0 flex w-full flex-col border-l border-line bg-surface shadow-ir-lg",
              width,
            )}
          >
            <header className="flex items-center justify-between gap-4 border-b border-line px-5 py-4">
              <h2 className="text-base font-semibold text-content">{title}</h2>
              <IconButton icon={X} label="Close" size="sm" onClick={onClose} />
            </header>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">{children}</div>
            {footer && (
              <footer className="flex gap-2 border-t border-line bg-surface-sunken px-5 py-4">
                {footer}
              </footer>
            )}
          </motion.aside>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
